'use client';

import type { BrandingFontFamily } from '@slims/shared/branding';
import { type BrandingEditorValues, BrandingEditorView } from '@slims/ui/organisms';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/utils/api';
import { useUIStore } from '@/stores/ui-store';

const EMPTY: BrandingEditorValues = {
	appName: '',
	tagline: '',
	logoUrl: '',
	primaryColor: '#00E5FF',
	secondaryColor: '#7C3AED',
	fontFamily: 'inter',
	showSlimsLogoOnLogin: true,
};

const FONTS: BrandingFontFamily[] = ['inter', 'system', 'lora', 'roboto', 'montserrat'];

function fromRow(row: Record<string, unknown> | null): BrandingEditorValues {
	if (!row || typeof row !== 'object') return EMPTY;
	const font = (row.font_family as string) ?? 'inter';
	return {
		appName: (row.app_name as string) ?? '',
		tagline: (row.tagline as string) ?? '',
		logoUrl: (row.logo_url as string) ?? '',
		primaryColor: (row.primary_color as string) ?? EMPTY.primaryColor,
		secondaryColor: (row.secondary_color as string) ?? EMPTY.secondaryColor,
		fontFamily: (FONTS.includes(font as BrandingFontFamily) ? font : 'inter') as BrandingFontFamily,
		showSlimsLogoOnLogin: (row.show_slims_logo_on_login ?? true) !== false,
	};
}

/**
 * Wiring dell'editor branding white-label della CER: carica
 * `/api/admin/orgs/:id/branding` (superadmin o admin della CER), passa i valori a
 * {@link BrandingEditorView} e persiste su «Salva» / «Ripristina». `orgId` è
 * sempre l'id della CER top-level (il branding non vive sulle configurazioni).
 *
 * Montato in un unico posto: la scheda «Personalizzazione» del dettaglio CER
 * (`/cer/orgs/:id/branding`), che serve sia il superadmin sia l'amministratore.
 */
export function CerBrandingPanel({ orgId, cerName }: { orgId: string; cerName?: string }) {
	const theme = useUIStore((s) => s.theme);
	const [value, setValue] = useState<BrandingEditorValues>(EMPTY);
	const [configured, setConfigured] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		let alive = true;
		setLoading(true);
		apiFetch(`/api/admin/orgs/${orgId}/branding`)
			.then((r) => (r.ok ? r.json() : null))
			.then((row: Record<string, unknown> | null) => {
				if (!alive) return;
				setConfigured(!!row && typeof row === 'object');
				setValue(fromRow(row));
			})
			.finally(() => {
				if (alive) setLoading(false);
			});
		return () => {
			alive = false;
		};
	}, [orgId]);

	const handleSave = useCallback(
		async (v: BrandingEditorValues) => {
			setSaving(true);
			try {
				const res = await apiFetch(`/api/admin/orgs/${orgId}/branding`, {
					method: 'PUT',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						app_name: v.appName.trim() || null,
						tagline: v.tagline.trim() || null,
						logo_url: v.logoUrl.trim() || null,
						primary_color: v.primaryColor,
						secondary_color: v.secondaryColor,
						font_family: v.fontFamily,
						show_slims_logo_on_login: v.showSlimsLogoOnLogin,
					}),
				});
				if (res.ok) {
					setValue(fromRow(await res.json().catch(() => null)));
					setConfigured(true);
					toast.success('Branding salvato');
				} else {
					toast.error('Errore nel salvataggio del branding');
				}
			} catch {
				toast.error('Errore di rete');
			} finally {
				setSaving(false);
			}
		},
		[orgId],
	);

	const handleUploadLogo = useCallback(
		async (file: File): Promise<string> => {
			const fd = new FormData();
			fd.append('file', file);
			const res = await apiFetch(`/api/admin/orgs/${orgId}/branding/logo`, {
				method: 'POST',
				body: fd,
			});
			const body = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
			if (!res.ok || !body?.url) {
				throw new Error(body?.error || 'Caricamento del logo non riuscito');
			}
			return body.url;
		},
		[orgId],
	);

	const handleReset = useCallback(async () => {
		setSaving(true);
		try {
			const res = await apiFetch(`/api/admin/orgs/${orgId}/branding`, { method: 'DELETE' });
			if (res.ok) {
				setValue(EMPTY);
				setConfigured(false);
				toast.success('Brand SLIMS CER ripristinato');
			} else {
				toast.error('Errore nel ripristino');
			}
		} catch {
			toast.error('Errore di rete');
		} finally {
			setSaving(false);
		}
	}, [orgId]);

	if (loading) return null;

	return (
		<BrandingEditorView
			value={value}
			configured={configured}
			saving={saving}
			cerName={cerName}
			colorScheme={theme === 'light' ? 'light' : 'dark'}
			onUploadLogo={handleUploadLogo}
			onSave={handleSave}
			onReset={handleReset}
		/>
	);
}
